/**
 * Typed client for the internal API.
 *
 * Two things it does that a bare fetch does not: it always sends the session cookie, and it
 * turns an RFC 9457 problem body into an Error carrying the machine readable code — so a screen
 * can react to `lesson_locked` without matching on a message that may be translated later.
 */

const BASE = '/app/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface Problem {
  code?: string;
  title?: string;
  detail?: string;
  errors?: { field?: string; code?: string; message?: string }[];
}

/**
 * Russian text for the failures a person is meant to read.
 *
 * The server answers in English with a machine readable `code`, which is the right split: its
 * messages are for logs and for other services, and it should not carry the interface's language.
 * Keying off the code rather than the sentence means the server can reword freely, and anything
 * not listed here still shows its original detail instead of a blank.
 */
const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Неверная почта или пароль.',
  not_authenticated: 'Нужно войти заново.',
  forbidden: 'Недостаточно прав.',
  lesson_locked: 'Урок пока закрыт.',
  not_enrolled: 'Вы не записаны на этот курс.',
  no_attempts_left: 'Попытки закончились.',
  attempt_finished: 'Эта попытка уже завершена.',
  time_is_up: 'Время вышло.',
  deadline_passed: 'Срок сдачи прошёл.',
  question_already_answered:
    'На вопрос уже отвечали, поэтому варианты заморожены. Сделайте копию и правьте её.',
  slot_already_answered:
    'На этот вопрос уже отвечали в этом тесте. Убрать его нельзя — изменились бы выставленные оценки.',
  course_has_certificates:
    'По этому курсу уже выданы сертификаты. Удалить его нельзя — иначе они перестанут проверяться.',
  course_has_student_work:
    'На этом курсе уже сдавали работы. Удалить его нельзя — скройте вместо удаления.',
  lesson_has_student_work:
    'В этот урок уже сдавали работы. Удалить его нельзя — очистите или скройте.',
  module_has_student_work:
    'В этом модуле уже сдавали работы. Удалить его нельзя — очистите или скройте.',
  slug_taken: 'Курс с таким адресом уже есть.',
  already_enrolled: 'Ученик уже зачислен на этот курс.',
  question_archived: 'Вопрос в архиве.',
  asset_not_scanned: 'Файл ещё проверяется, попробуйте чуть позже.',
  rate_limited: 'Слишком часто. Подождите немного.',
  /* The code the server actually sends for throttling. The key beside it was never once
     matched, so a throttled person read the server's English sentence instead. */
  rate_limit_exceeded: 'Слишком часто. Подождите немного.',
  daily_limit_reached: 'Вопросы помощнику на сегодня закончились. Новые появятся после полуночи.',
  account_locked: 'Слишком много неудачных попыток. Подождите и попробуйте снова.',
  account_disabled: 'Аккаунт отключён. Обратитесь к администратору школы.',
  already_member: 'Этот человек уже состоит в школе.',
  invitation_not_found: 'Приглашение не найдено.',
  role_not_grantable: 'Ваша роль не позволяет выдавать такую роль.',
  cannot_demote_self: 'Нельзя понизить самого себя.',
  cannot_change_owner: 'Роль владельца меняет только сам владелец.',
  last_owner: 'Это последний владелец — школа не может остаться без него.',
  member_not_found: 'Участник не найден.',
  not_a_student: 'Этот раздел только для учеников.',
  course_not_completed: 'Курс ещё не пройден — перепрохождение назначать рано.',
  certificate_not_found: 'Сертификат не найден.',
  certificate_already_revoked: 'Сертификат уже отозван.',
  enrollment_not_found: 'Зачисление не найдено.',
  student_not_found: 'Ученик не найден.',
  scanner_unavailable: 'Антивирус недоступен, файл не проверен. Попробуйте позже.',
  file_too_large_to_scan: 'Файл слишком большой для проверки антивирусом. Разделите его на части.',
  insufficient_role: 'Недостаточно прав для этого действия.',
  registration_closed: 'Регистрация закрыта. Нужен код от администратора школы.',
  signup_closed: 'Регистрация сейчас закрыта. Попросите доступ у преподавателя.',
  organization_not_found: 'Школа не найдена. Обратитесь к преподавателю.',
  /* Codes the three email flows can answer with. Taken from the server, not guessed: an English
     sentence on a Russian screen is the tell that a code was invented rather than looked up. */
  invalid_code: 'Код неверный или устарел. Запросите новый.',
  email_not_verified: 'Сначала подтвердите почту — мы отправили код.',
  invalid_reset_token: 'Ссылка для смены пароля недействительна или уже использована. Запросите новую.',
  invalid_verification_token: 'Ссылка подтверждения недействительна или уже использована.',
  invalid_invitation: 'Приглашение недействительно, истекло или уже использовано.',
  account_details_required: 'Для нового аккаунта нужны имя и пароль.',
  email_taken: 'Эта почта уже занята. Пригласите этого человека или возьмите другой адрес.',
  slug_taken_org: 'Организация с таким адресом уже есть.',

  /* Field level codes from a 422. These are the ones a person actually triggers. */
  extension_missing: 'У файла нет расширения.',
  extension_not_allowed: 'Такой тип файла не принимается.',
  mime_mismatch: 'Содержимое файла не совпадает с расширением.',
  empty: 'Файл пустой.',
  too_large: 'Файл слишком большой.',
  infected: 'Файл не прошёл проверку антивирусом.',
};

/**
 * A validation failure carries `validation_error` at the top and the useful code in `errors[0]`,
 * so the specific one wins. Everything else is keyed by the top-level code.
 */
/** The teacher's own words for the fields they filled in, so a refusal names something they typed. */
const FIELD_NAMES: Record<string, string> = {
  title: 'Название',
  description_md: 'Что нужно сделать',
  due_at: 'Срок сдачи',
  max_files: 'Сколько файлов',
  max_file_mb: 'Размер файла',
  max_attempts: 'Попыток',
  max_score: 'Максимальный балл',
  pass_fraction: 'Проходной балл',
  late_policy: 'Если сдали позже срока',
  watched_seconds: 'Просмотрено секунд',
  duration_seconds: 'Длительность видео',
  text: 'Ответ',
  body: 'Сообщение',
  email: 'Почта',
  password: 'Пароль',
  full_name: 'Имя и фамилия',
  slug: 'Адрес',
};

/**
 * Zod's own words for why a field was refused, in Russian.
 *
 * These codes come straight from the validator, so its English arrives with them: a teacher who
 * saved a settings screen was shown "Поле «Попыток»: Expected number, received null", which names
 * a type system rather than anything they could act on. Translated here rather than in MESSAGES
 * because the field name has to survive — "заполнено неверно" without saying which box is worse
 * than English that at least came with one.
 */
const ISSUE_MESSAGES: Record<string, string> = {
  invalid_type: 'заполните это поле.',
  too_small: 'значение слишком маленькое.',
  too_big: 'значение слишком большое.',
  invalid_string: 'значение записано не в том виде.',
  invalid_enum_value: 'выберите значение из списка.',
};

function problemFrom(response: Response, body: unknown): ApiError {
  const problem = (body ?? {}) as Problem;
  const code = problem.code ?? 'unknown';
  const fieldCode = problem.errors?.[0]?.code;

  const known = (fieldCode ? MESSAGES[fieldCode] : undefined) ?? MESSAGES[code];

  /*
   * When nothing is translated, say which field was refused rather than "one or more fields
   * failed validation" — a sentence that tells a teacher staring at nine inputs precisely
   * nothing, and cost an afternoon of guessing to get behind once already.
   */
  const field = problem.errors?.[0]?.field;
  const fallback =
    field && field !== '(root)'
      ? `Поле «${FIELD_NAMES[field] ?? field}»: ${
          (fieldCode ? ISSUE_MESSAGES[fieldCode] : undefined) ??
          problem.errors?.[0]?.message ??
          'неверное значение'
        }`
      : (problem.detail ?? problem.title ?? 'Что-то пошло не так');

  const message = known ?? fallback;

  return new ApiError(response.status, fieldCode ?? code, message);
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    // The session lives in an httpOnly cookie; nothing here ever touches a token.
    credentials: 'include',
    headers: init.body ? { 'Content-Type': 'application/json' } : {},
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) throw problemFrom(response, body);

  return (body as { data: T }).data;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  call<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

const patch = <T>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

const del = (path: string): Promise<void> => call<void>(path, { method: 'DELETE' });

/** Collection endpoints answer with a cursor envelope; screens only need the rows. */
async function list<T>(path: string): Promise<T[]> {
  const response = await fetch(`${BASE}${path}`, { credentials: 'include' });

  if (!response.ok) {
    throw problemFrom(response, await response.json().catch(() => ({})));
  }

  const body = (await response.json()) as { data: T[] };
  return body.data;
}

/* ---- types ------------------------------------------------------------ */

export type OrgRole = 'owner' | 'admin' | 'teacher' | 'student' | 'client_observer';

/** What every code-sending endpoint answers with. */
export interface CodeSent {
  email: string;
  code_required: true;
  expires_in_minutes: number;
  /** False when the letter could not be sent — then nobody can finish, and the screen must say so. */
  email_delivered: boolean;
}

/** Tells a "we sent a code" answer from a finished sign-in. */
export function isCodeSent(value: Me | CodeSent): value is CodeSent {
  return 'code_required' in value;
}

export interface Me {
  profile_id: string;
  email: string;
  full_name: string;
  email_verified: boolean;
  organizations: { id: string; name: string; slug: string; role: OrgRole }[];
}

/** Owners, admins and teachers get the authoring side; everybody else only learns. */
export function isStaff(me: Me): boolean {
  const role = me.organizations[0]?.role;
  return role === 'owner' || role === 'admin' || role === 'teacher';
}

export interface EnrolledCourse {
  enrollment_id: string;
  course_id: string;
  title: string;
  slug: string;
  summary: string | null;
  progress_percent: number;
  status: string;
  current_lesson_id: string | null;
}

export interface CourseLesson {
  id: string;
  title: string;
  position: number;
  estimated_minutes: number | null;
  state: string;
  available: boolean;
  locked_reasons: string[];
}

export interface CourseModule {
  id: string;
  title: string;
  position: number;
  available: boolean;
  completed: boolean;
  locked_reasons: string[];
  lessons: CourseLesson[];
}

export interface CourseDetail {
  id: string;
  title: string;
  summary: string | null;
  progression_mode: string;
  progress_percent: number;
  modules: CourseModule[];
}

export interface LessonBlock {
  id: string;
  type: string;
  position: number;
  config: Record<string, unknown>;
  ref_id: string | null;
  watched_fraction?: number;
  embed_url?: string;
  /** The attached file's real type, so the page knows whether it can show it. */
  asset_mime?: string;
  asset_name?: string;
  /** True when the lesson can display the file — itself, or through a rendering. */
  asset_viewable?: boolean;
}

export interface EditorNode {
  type?: string;
  text?: string;
  content?: EditorNode[];
}

export interface LessonDetail {
  id: string;
  course_id: string;
  module_id: string;
  title: string;
  content: EditorNode;
  state: string;
  completion: { type: string; met: boolean }[];
  blocks: LessonBlock[];
  /** Where the course goes after this one, or null when this is the last lesson in it. */
  next: { id: string; title: string } | null;
}

export interface ProgressUpdate {
  lesson_state: string;
  lesson_completed: boolean;
  module_completed: boolean;
  progress_percent: number;
  course_completed: boolean;
  /** Set only on the call that issued it, so the learner is congratulated exactly once. */
  certificate_number?: string;
}

export interface SubmissionFile {
  id: string;
  asset_id: string;
  name: string;
  size_bytes: number;
}

export interface Submission {
  id: string;
  attempt_number: number;
  status: string;
  text: string | null;
  is_late: boolean;
  final_fraction: number | null;
  score: number | null;
  feedback: string | null;
  submitted_at: string | null;
  files: SubmissionFile[];
}

export interface Assignment {
  id: string;
  lesson_id: string;
  title: string;
  description_md: string;
  due_at: string | null;
  max_score: number;
  pass_fraction: number;
  allow_text: boolean;
  allow_files: boolean;
  max_files: number;
  max_file_mb: number;
  max_attempts: number;
  late_policy: string;
}

export interface UploadedAsset {
  id: string;
  kind: string;
  original_name: string;
  size_bytes: number;
  mime: string;
  scan_status: string;
  deduplicated: boolean;
}

export interface QuizStatus {
  quiz_id: string;
  title: string;
  pass_fraction: number;
  max_attempts: number;
  time_limit_min: number | null;
  question_count: number;
  attempts_used: number;
  attempts_left: number | null;
  best_fraction: number | null;
  passed: boolean;
  can_review: boolean;
  open_attempt_id: string | null;
  last_attempt_id: string | null;
}

export type QuestionType =
  | 'single'
  | 'multi'
  | 'text'
  | 'number'
  | 'ordering'
  | 'fill'
  | 'file'
  | 'code';

/** Mirrors the AnswerData union the scorer accepts; anything else is rejected at the boundary. */
export type Answer =
  | { type: 'single'; optionId: string }
  | { type: 'multi'; optionIds: string[] }
  | { type: 'text'; value: string }
  | { type: 'number'; value: number }
  | { type: 'ordering'; optionIds: string[] }
  | { type: 'fill'; values: string[] }
  | { type: 'file'; assetId: string }
  | { type: 'code'; source: string; language: string };

export interface AttemptQuestion {
  slot_id: string;
  question_id: string;
  type: QuestionType;
  text_md: string;
  max_mark: number;
  options: { id: string; label: string }[];
  answer: Answer | null;
  blank_count?: number;
  /* Review only. */
  fraction?: number;
  needs_manual_review?: boolean;
  explanation_md?: string | null;
  correct_option_ids?: string[];
}

export interface Attempt {
  id: string;
  quiz_id: string;
  attempt_number: number;
  state: 'in_progress' | 'submitted' | 'graded' | 'abandoned';
  started_at: string;
  deadline_at: string | null;
  fraction: number | null;
  passed: boolean | null;
  review_available?: boolean;
  questions: AttemptQuestion[];
}

export interface AttemptResult {
  attempt_id: string;
  fraction: number;
  passed: boolean;
  points: number;
  max_points: number;
  needs_manual_review: boolean;
}

/* ---- authoring types -------------------------------------------------- */

export interface AuthoredCourse {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  level: string | null;
  tags: string[];
  progressionMode: string;
  isPublished: boolean;
  publishedAt: string | null;
  issuesCertificate: boolean;
  certificateValidMonths: number | null;
  createdAt: string;
}

export interface CourseTree {
  course: AuthoredCourse;
  modules: {
    id: string;
    title: string;
    position: number;
    lessons: { id: string; title: string; position: number }[];
  }[];
}

export interface AuthoredBlock {
  /** Characters the assistant read out of an attached file; 0 means it could not read it. */
  assistant_chars?: number | null;
  id: string;
  type: string;
  position: number;
  config: Record<string, unknown>;
  ref_id: string | null;
  ref_title: string | null;
}

export interface CompletionCondition {
  /** 'material' is a presentation, a file or an image the learner is expected to open. */
  type: 'view' | 'video' | 'assignment' | 'quiz' | 'material';
  block_id?: string;
  assignment_id?: string;
  quiz_id?: string;
  threshold_percent?: number;
  min_state?: 'submitted' | 'graded';
  min_fraction?: number;
}

export interface CompletionPolicy {
  require: 'all' | 'any';
  conditions: CompletionCondition[];
}

export interface AuthoredLesson {
  id: string;
  course_id: string;
  course_title: string;
  module_id: string;
  module_title: string;
  title: string;
  position: number;
  content: EditorNode;
  completion_policy: CompletionPolicy;
  estimated_minutes: number | null;
  blocks: AuthoredBlock[];
}

export interface BankQuestion {
  id: string;
  type: QuestionType;
  text_md: string;
  explanation_md: string | null;
  config: Record<string, unknown>;
  is_archived: boolean;
  used_in_quizzes: number;
  options: { id: string; label: string; is_correct: boolean; position: number }[];
}

export interface QuizDetail {
  id: string;
  lesson_id: string | null;
  title: string;
  pass_fraction: number;
  max_attempts: number;
  time_limit_min: number | null;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  show_review: 'never' | 'after_pass' | 'always';
  attempt_count: number;
  slots: {
    id: string;
    question_id: string;
    position: number;
    max_mark: number;
    type: QuestionType;
    text_md: string;
  }[];
}

export interface QueueItem extends Submission {
  assignment_id: string;
  student_name: string;
  assignment_title: string;
  lesson_title: string;
}

export interface Certificate {
  id: string;
  number: string;
  course_id: string;
  enrollment_id: string;
  course_title: string;
  student_name: string;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  is_expired: boolean;
  is_valid: boolean;
  score: number | null;
  verify_url: string;
}

/** What the public check returns. Deliberately no grade — see spec section 5. */
export interface VerifiedCertificate {
  number: string;
  full_name: string;
  course_title: string;
  organization_name: string;
  issued_at: string;
  expires_at: string | null;
  is_revoked: boolean;
}

export interface ChatMessage {
  id: string;
  body: string;
  created_at: string;
  from_student: boolean;
  sender_name: string;
  /** Whether the other side has opened the thread since this was written. */
  read_by_other: boolean;
}

export interface Thread {
  conversation_id: string;
  student_profile_id: string;
  student_name: string;
  messages: ChatMessage[];
}

export interface ConversationSummary {
  conversation_id: string | null;
  student_profile_id: string;
  student_name: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_from_student: boolean | null;
  unread: number;
  /** When this pupil was last on the site — null if never. */
  last_seen_at: string | null;
}

export interface StudentCourse {
  enrollment_id: string;
  course_id: string;
  title: string;
  progress_percent: number;
  status: string;
}

export interface AiTurn {
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

export interface StudentEventView {
  kind: 'invited' | 'joined' | 'signed_in' | 'submitted' | 'quiz_finished' | 'lesson_done' | 'certified';
  at: string;
  subject: string | null;
}

export interface ActivityDetail {
  kind: 'video' | 'assignment' | 'quiz';
  title: string;
  state: 'done' | 'partial' | 'waiting' | 'returned' | 'pending';
  percent: number | null;
  /** For a quiz: which one, and how many tries are left. Null when the quiz has no limit. */
  ref_id?: string;
  attempts_left?: number | null;
}

/**
 * One lesson on the teacher's view of a pupil, which is a different thing from the lesson page.
 *
 * Named apart from LessonDetail on purpose. Both were called that, and TypeScript does not object
 * to two interfaces sharing a name — it merges them, so each side silently claimed the other's
 * fields and the compiler stopped being able to catch a response that had gone astray.
 */
export interface LessonProgress {
  id: string;
  title: string;
  state: string;
  /** Share of this lesson done, 0..1 — so the course figure can be checked, not just believed. */
  fraction: number;
  activities: ActivityDetail[];
}

export interface ModuleDetail {
  id: string;
  title: string;
  completed: boolean;
  lessons: LessonProgress[];
}

export interface CourseDetailProgress {
  enrollment_id: string;
  course_id: string;
  title: string;
  progress_percent: number;
  status: string;
  modules: ModuleDetail[];
}

export interface Student {
  profile_id: string;
  full_name: string;
  email: string;
  status: string;
  joined_at: string;
  /** Null when they have never signed in — the state worth spotting in a list. */
  last_seen_at: string | null;
  courses: StudentCourse[];
}

export interface CreatedStudent {
  profile_id: string;
  full_name: string;
  email: string;
  password: string;
  /** False when the letter could not be sent — then the teacher has to pass the login on by hand. */
  email_delivered: boolean;
}

export interface Member {
  id: string;
  profile_id: string;
  full_name: string;
  email: string;
  role: OrgRole;
  status: string;
}

/* ---- calls ------------------------------------------------------------ */

/**
 * One line of the bell: something that happened to you, and where to go about it.
 *
 * The same shape for a pupil and a teacher. What separates them is which notices exist for their
 * profile, decided on the server, never here.
 */
export interface Notice {
  id: string;
  type: string;
  title: string;
  body: string | null;
  /** An in-app path to act on this, when there is one to point at. */
  link: string | null;
  created_at: string;
  read: boolean;
}

/**
 * One line of the standings.
 *
 * The parts travel with the total so the table can show what the points were made of. A score
 * nobody can take apart is a score nobody believes.
 */
export interface Standing {
  profile_id: string;
  name: string;
  place: number;
  score: number;
  lessons_done: number;
  lessons_total: number;
  /** Share of everything the course asks for that has been done, 0..1. */
  completion: number;
  /** Null until they have answered a first question. */
  accuracy: number | null;
  quizzes_passed: number;
  quizzes_total: number;
  parts: { completion: number; accuracy: number };
}

export interface Leaderboard {
  /** Where the person asking stands, or null when they are not a ranked learner. */
  you: { place: number; score: number } | null;
  total: number;
  standings: Standing[];
}

export const api = {
  me: () => call<Me>('/me'),

  /**
   * Signing in. May answer with a session, or with "we sent a code" — the browser tells them
   * apart by `code_required`, because a password alone is no longer always enough.
   */
  login: (email: string, password: string) =>
    post<Me | CodeSent>('/auth/login', { email, password }),

  logout: () => post<void>('/auth/logout'),

  /** The standings, and where the person asking stands in them. */
  leaderboard: () => call<Leaderboard>('/learn/leaderboard'),

  /* the bell ---------------------------------------------------------------- */

  /**
   * What has happened to the signed-in person, and how much of it they have not seen.
   *
   * The count is not the length of the list: the list stops at a page, the badge does not.
   */
  notifications: () => call<{ unread: number; notices: Notice[] }>('/notifications'),

  /** Opening the bell is what marks it read; there is no separate button to forget to press. */
  readNotifications: () => post<{ read: number }>('/notifications/read'),

  /** The server's public key, or null when this school has push turned off. */
  pushKey: () => call<{ key: string | null }>('/notifications/push/key'),

  /** Where to send: whatever the browser's push service handed the subscription. */
  pushSubscribe: (subscription: Record<string, unknown>) =>
    call<void>('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  pushUnsubscribe: (endpoint: string) =>
    call<void>('/notifications/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),

  /* the three flows that arrive by email ---------------------------------- */

  /** Always 202, whether or not the address is known: the answer must not reveal who is here. */
  forgotPassword: (email: string) => post<void>('/auth/password/forgot', { email }),

  resetPassword: (email: string, code: string, password: string) =>
    post<void>('/auth/password/reset', { email, code, password }),

  verifyEmail: (token: string) => post<void>('/auth/email/verify', { token }),

  acceptInvitation: (body: { token: string; full_name?: string; password?: string }) =>
    post<{ organization_id: string; role: string; created_account: boolean }>(
      '/invitations/accept',
      body,
    ),

  /**
   * Creates an organization and its owner. Gated by a code the operator hands out — this builds a
   * whole tenant, so it is not a form anybody who finds the address should be able to complete.
   */
  /**
   * A pupil signing themselves up. Answers with "a code is on its way", not with a session: the
   * account exists but stays inert until the code comes back.
   */
  signUp: (body: { email: string; password: string; full_name: string }) =>
    post<CodeSent>('/auth/signup', body),

  verifySignup: (email: string, code: string) => post<Me>('/auth/signup/verify', { email, code }),

  /** The second step of a login, when the server asked for one. */
  verifyLogin: (email: string, code: string, rememberDevice: boolean) =>
    post<Me>('/auth/login/verify', { email, code, remember_device: rememberDevice }),

  register: (body: {
    email: string;
    password: string;
    full_name: string;
    organization_name: string;
    organization_slug: string;
    registration_code?: string;
  }) => post<Me>('/auth/register', body),

  myCourses: () => list<EnrolledCourse>('/learn/courses'),

  course: (courseId: string) => call<CourseDetail>(`/learn/courses/${courseId}`),

  lesson: (lessonId: string) => call<LessonDetail>(`/learn/lessons/${lessonId}`),

  markViewed: (lessonId: string) => post<ProgressUpdate>(`/learn/lessons/${lessonId}/view`),

  /** Tells the server the learner opened a material, which is what "прочитал" can honestly mean. */
  materialSeen: (blockId: string) =>
    post<{ lesson_completed: boolean; progress_percent: number }>(`/learn/blocks/${blockId}/seen`),

  videoProgress: (
    blockId: string,
    body: { watched_seconds: number; furthest_seconds: number; duration_seconds: number },
  ) => post<ProgressUpdate>(`/learn/blocks/${blockId}/video-progress`, body),

  /* homework ------------------------------------------------------------ */

  assignment: (assignmentId: string) => call<Assignment>(`/learn/assignments/${assignmentId}`),

  submissions: (assignmentId: string) =>
    list<Submission>(`/learn/assignments/${assignmentId}/submissions`),

  submit: (assignmentId: string, body: { text?: string; asset_ids?: string[] }) =>
    post<Submission>(`/learn/assignments/${assignmentId}/submissions`, body),

  /**
   * Uploads one file.
   *
   * multipart, so no JSON content type here: the browser has to set the boundary itself. A 202
   * means the file landed but a virus scan has not answered yet — not an error, but not
   * downloadable either, which is why the caller is shown `scan_status`.
   */
  upload: async (file: File): Promise<UploadedAsset> => {
    const form = new FormData();
    form.append('file', file);

    const response = await fetch(`${BASE}/assets`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });

    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) throw problemFrom(response, body);

    return (body as { data: UploadedAsset }).data;
  },

  assetUrl: (assetId: string) => `${BASE}/assets/${assetId}`,

  /**
   * The same file, asked for as something to look at rather than something to keep.
   *
   * The server still decides: it only honours this for types a browser renders safely, and
   * answers with an attachment for everything else.
   */
  assetViewUrl: (assetId: string) => `${BASE}/assets/${assetId}?view=1`,

  /* the study assistant ---------------------------------------------------- */

  /** What the pupil has already asked about this lesson, and whether the assistant is on. */
  aiConversation: (lessonId: string) =>
    call<{ enabled: boolean; messages: AiTurn[] }>(`/learn/lessons/${lessonId}/ai`),

  askAi: (lessonId: string, question: string) =>
    post<{ text: string; used_passages: number; remaining_today: number }>(
      `/learn/lessons/${lessonId}/ai`,
      { question },
    ),

  /* messages -------------------------------------------------------------- */

  myThread: () => call<Thread>('/learn/messages'),
  sendMyMessage: (body: string) => post<ChatMessage>('/learn/messages', { body }),
  myUnread: () => call<{ unread: number }>('/learn/messages/unread'),

  /* certificates --------------------------------------------------------- */

  myCertificates: () => list<Certificate>('/me/certificates'),

  /**
   * The printable sheet is fetched by the browser, not by this client: the response is a file
   * with a Content-Disposition, so a plain link downloads it under the right name and the session
   * cookie rides along. Pulling it through fetch would only mean rebuilding that by hand.
   */
  certificatePdfUrl: (certificateId: string) => `${BASE}/me/certificates/${certificateId}/pdf`,

  /** Public: works without a session, which is the whole point of a certificate check. */
  verifyCertificate: (number: string) =>
    call<VerifiedCertificate>(`/verify/${encodeURIComponent(number)}`),

  /* quizzes ------------------------------------------------------------- */

  quizStatus: (quizId: string) => call<QuizStatus>(`/learn/quizzes/${quizId}`),

  startAttempt: (quizId: string) => post<Attempt>(`/learn/quizzes/${quizId}/attempts`),

  attempt: (attemptId: string) => call<Attempt>(`/learn/attempts/${attemptId}`),

  answer: (attemptId: string, slotId: string, answer: Answer) =>
    patch<void>(`/learn/attempts/${attemptId}/answers/${slotId}`, answer),

  submitAttempt: (attemptId: string) => post<AttemptResult>(`/learn/attempts/${attemptId}/submit`),

  /* authoring ----------------------------------------------------------- */

  authoring: {
    courses: () => list<AuthoredCourse>('/authoring/courses'),

    createCourse: (body: {
      title: string;
      slug: string;
      summary?: string | null;
      progression_mode?: string;
    }) => post<AuthoredCourse>('/authoring/courses', body),

    updateCourse: (
      courseId: string,
      body: {
        title?: string;
        summary?: string | null;
        progression_mode?: string;
        issues_certificate?: boolean;
        certificate_valid_months?: number | null;
      },
    ) => patch<AuthoredCourse>(`/authoring/courses/${courseId}`, body),

    courseTree: (courseId: string) => call<CourseTree>(`/authoring/courses/${courseId}`),

    publish: (courseId: string, published: boolean) =>
      post<AuthoredCourse>(`/authoring/courses/${courseId}/${published ? 'publish' : 'unpublish'}`),

    createModule: (courseId: string, title: string) =>
      post<{ id: string }>(`/authoring/courses/${courseId}/modules`, { title }),

    updateModule: (moduleId: string, body: { title?: string }) =>
      patch<{ id: string }>(`/authoring/modules/${moduleId}`, body),

    deleteCourse: (courseId: string) => del(`/authoring/courses/${courseId}`),

    deleteModule: (moduleId: string) => del(`/authoring/modules/${moduleId}`),

    createLesson: (moduleId: string, title: string) =>
      post<{ id: string }>(`/authoring/modules/${moduleId}/lessons`, { title }),

    lesson: (lessonId: string) => call<AuthoredLesson>(`/authoring/lessons/${lessonId}`),

    updateLesson: (
      lessonId: string,
      body: {
        title?: string;
        content?: EditorNode;
        estimated_minutes?: number | null;
        completion_policy?: CompletionPolicy;
      },
    ) => patch<{ id: string }>(`/authoring/lessons/${lessonId}`, body),

    deleteLesson: (lessonId: string) => del(`/authoring/lessons/${lessonId}`),

    createBlock: (
      lessonId: string,
      body: { type: string; config?: Record<string, unknown>; ref_id?: string | null },
    ) => post<AuthoredBlock>(`/authoring/lessons/${lessonId}/blocks`, body),

    updateBlock: (
      blockId: string,
      body: { config?: Record<string, unknown>; ref_id?: string | null },
    ) => patch<AuthoredBlock>(`/authoring/blocks/${blockId}`, body),

    deleteBlock: (blockId: string) => del(`/authoring/blocks/${blockId}`),

    reorder: (entity: 'module' | 'lesson' | 'block', parentId: string, order: string[]) =>
      post<void>('/authoring/reorder', { entity, parent_id: parentId, order }),

    attach: (body: { asset_id: string; target_type: string; target_id: string; slot?: string }) =>
      post<void>('/assets/attach', body),

    /* question bank ---------------------------------------------------- */

    /** One question with its options, for the screen that edits it. */
    question: (questionId: string) => call<BankQuestion>(`/authoring/questions/${questionId}`),

    questions: (search?: string) =>
      list<BankQuestion>(
        `/authoring/questions${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),

    createQuestion: (body: {
      type: QuestionType;
      text_md: string;
      explanation_md?: string | null;
      config?: Record<string, unknown>;
      options?: { label: string; is_correct?: boolean }[];
    }) => post<{ id: string }>('/authoring/questions', body),

    updateQuestion: (
      questionId: string,
      body: {
        text_md?: string;
        explanation_md?: string | null;
        config?: Record<string, unknown>;
        options?: { label: string; is_correct?: boolean }[];
      },
    ) => patch<{ id: string }>(`/authoring/questions/${questionId}`, body),

    duplicateQuestion: (questionId: string) =>
      post<{ id: string }>(`/authoring/questions/${questionId}/duplicate`),

    archiveQuestion: (questionId: string, archived: boolean) =>
      post<void>(`/authoring/questions/${questionId}/archive`, { archived }),

    /* quizzes and homework --------------------------------------------- */

    createQuiz: (lessonId: string, body: { title: string }) =>
      post<{ id: string }>(`/authoring/lessons/${lessonId}/quizzes`, body),

    quiz: (quizId: string) => call<QuizDetail>(`/authoring/quizzes/${quizId}`),

    updateQuiz: (
      quizId: string,
      body: {
        title: string;
        pass_fraction?: number;
        max_attempts?: number;
        time_limit_min?: number | null;
        shuffle_questions?: boolean;
        shuffle_options?: boolean;
        show_review?: 'never' | 'after_pass' | 'always';
      },
    ) => patch<{ id: string }>(`/authoring/quizzes/${quizId}`, body),

    addSlot: (quizId: string, questionId: string, maxMark?: number) =>
      post<{ id: string }>(`/authoring/quizzes/${quizId}/slots`, {
        question_id: questionId,
        ...(maxMark === undefined ? {} : { max_mark: maxMark }),
      }),

    removeSlot: (slotId: string) => del(`/authoring/slots/${slotId}`),

    createAssignment: (lessonId: string, body: Record<string, unknown>) =>
      post<Assignment>(`/authoring/lessons/${lessonId}/assignments`, body),

    /**
     * The teacher's own view of an assignment.
     *
     * Not api.assignment — that one is the learner's, and it checks enrolment. A teacher is never
     * enrolled in the course they built, so asking there told them they were not on it.
     */
    assignment: (assignmentId: string) =>
      call<Assignment>(`/authoring/assignments/${assignmentId}`),

    updateAssignment: (assignmentId: string, body: Record<string, unknown>) =>
      patch<Assignment>(`/authoring/assignments/${assignmentId}`, body),

    /* teaching --------------------------------------------------------- */

    queue: (checked = false) =>
      list<QueueItem>(`/teach/queue${checked ? '?checked=true' : ''}`),

    grade: (submissionId: string, fraction: number, feedback: string | null) =>
      post<Submission>(`/teach/submissions/${submissionId}/grade`, { fraction, feedback }),

    returnWork: (submissionId: string, feedback: string) =>
      post<Submission>(`/teach/submissions/${submissionId}/return`, { feedback }),

    certificates: (courseId?: string) =>
      list<Certificate>(
        `/teach/certificates${courseId ? `?course_id=${encodeURIComponent(courseId)}` : ''}`,
      ),

    revokeCertificate: (certificateId: string, reason: string) =>
      post<void>(`/teach/certificates/${certificateId}/revoke`, { reason }),

    certificatePdfUrl: (certificateId: string) =>
      `${BASE}/teach/certificates/${certificateId}/pdf`,

    /** Opens the next cycle: the learner takes the course again and earns a new certificate. */
    retake: (enrollmentId: string) =>
      post<{ cycle_number: number }>(`/teach/enrollments/${enrollmentId}/retake`),

    members: (orgId: string) => list<Member>(`/orgs/${orgId}/members`),

    /* students -------------------------------------------------------------- */

    students: () => list<Student>('/teach/students'),

    /** Removes a pupil's access. Their submitted work and certificates stay. */
    removeStudent: (profileId: string) => del(`/teach/students/${profileId}`),

    /**
     * Takes a pupil off one course, and says what that turned out to mean.
     *
     * An enrolment nobody has used disappears; one with work behind it is cancelled and keeps the
     * work. The server decides which, because only it can see whether anything was handed in.
     */
    endEnrollment: (enrollmentId: string) =>
      call<{ ending: 'removed' | 'cancelled' }>(`/teach/enrollments/${enrollmentId}`, {
        method: 'DELETE',
      }),

    /** One pupil's story: signed in, handed work in, finished a lesson, earned a certificate. */
    studentEvents: (profileId: string) =>
      list<StudentEventView>(`/teach/students/${profileId}/events`),

    /** One pupil's work in detail: modules, lessons, and what was done inside each. */
    studentProgress: (profileId: string) =>
      list<CourseDetailProgress>(`/teach/students/${profileId}/progress`),

    conversations: () => list<ConversationSummary>('/teach/conversations'),

    unread: () => call<{ unread: number }>('/teach/messages/unread'),

    /** Gives a learner more tries at a quiz. Recorded as a grant, not by rewriting their work. */
    grantAttempts: (quizId: string, profileId: string, extra: number) =>
      post<{ allowed: number; used: number }>(`/teach/quizzes/${quizId}/attempts`, {
        profile_id: profileId,
        extra,
      }),

    thread: (studentProfileId: string) => call<Thread>(`/teach/conversations/${studentProfileId}`),

    reply: (studentProfileId: string, body: string) =>
      post<ChatMessage>(`/teach/conversations/${studentProfileId}`, { body }),

    /**
     * The password comes back exactly once, in this response. It is not stored anywhere readable,
     * so the screen has to show it while it can.
     */
    createStudent: (body: { email: string; full_name: string; password?: string }) =>
      post<CreatedStudent>('/teach/students', body),

    enroll: (profileId: string, courseId: string) =>
      post<{ id: string }>('/teach/enrollments', { profile_id: profileId, course_id: courseId }),
  },
};

/** Flattens the editor document into paragraphs. Enough for prose; rich media arrives as blocks. */
export function documentToParagraphs(doc: EditorNode | null | undefined): string[] {
  if (!doc?.content) return [];

  return doc.content
    .map((node) => (node.content ?? []).map((child) => child.text ?? '').join(''))
    .filter((line) => line.trim().length > 0);
}

/** The inverse: plain paragraphs back into the document shape the API stores. */
export function paragraphsToDocument(text: string): EditorNode {
  return {
    type: 'doc',
    content: text
      .split(/\n{2,}/u)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => ({ type: 'paragraph', content: [{ type: 'text', text: line }] })),
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
